import {
  ConversationContext,
  ConversationInlineData,
  ConversationMessage,
  ConversationMessageStatus,
  MessagePartData,
  ConversationParticipant,
  ConversationStatus,
  StartConversationParams,
} from '@stripe-internal/conversations-client';
import {
  ConversationsApiDataConversation,
  ConversationsApiDataMessage,
  ConversationsApiDataMessagePart,
  ConversationsApiDataMessagePartContentSelectedOptionPartContent,
  ConversationsApiDataParticipant,
} from '../generated/types';
import {ErrorResponse, ReportWarning, UNAUTHORIZED_ERROR} from './ApiMethods';
import {withConversationOverview} from '../conversationOverview';

type FrameworkConversationData = {
  id: string;
  status: ConversationStatus;
  messages: Record<string, ConversationMessage>;
  participants: Record<string, ConversationParticipant>;
  context: ConversationContext;
};

type RacDetails = {
  userName: string;
  userPhoneNumber: string;
  callCanceled: false;
};

export type PollInputModes = {
  enabledInputModes: string[];
  messageId: string;
};

export class FrameworkAdapter {
  private static reportWarning(
    reporter: ReportWarning | undefined,
    error: string,
    tags: Record<string, string | number | boolean | undefined>,
  ): void {
    if (!reporter) {
      return;
    }
    const filteredTags: Record<string, string | number | boolean> = {};
    Object.entries(tags).forEach(([key, value]) => {
      if (value !== undefined) {
        filteredTags[key] = value;
      }
    });
    reporter(error, {tags: filteredTags});
  }

  static toClientErrorResponse(response: ErrorResponse): {
    code: number;
    error: string;
  } {
    if (response.error === UNAUTHORIZED_ERROR) {
      return {code: 401, error: UNAUTHORIZED_ERROR};
    }
    return {code: response.code, error: response.error};
  }

  private static roleFromType(type?: string): ConversationParticipant['role'] {
    const normalizedType = type?.toLowerCase();

    switch (normalizedType) {
      case 'human_agent':
        return 'agent';
      case 'bot':
      case 'assistant':
      case 'system':
        return 'bot';
      case 'user':
      default:
        return 'user';
    }
  }

  static participantTypeFromRole(
    role: ConversationParticipant['role'],
  ): string {
    if (role === 'agent') {
      return 'human_agent';
    }
    if (role === 'bot') {
      return 'assistant';
    }
    return 'user';
  }

  private static displayNameForRole(
    role: ConversationParticipant['role'],
  ): string {
    if (role === 'agent') {
      return 'Agent';
    }
    if (role === 'bot') {
      return 'Assistant';
    }
    return 'User';
  }

  private static getOrBuildParticipant(
    participantsById: Record<string, ConversationParticipant>,
    id: string,
    role: ConversationParticipant['role'],
    displayName?: string,
  ): ConversationParticipant {
    if (!participantsById[id]) {
      participantsById[id] = {
        id,
        role,
        displayName: displayName || this.displayNameForRole(role),
      };
    }

    return participantsById[id];
  }

  private static apiParticipantToFrameworkParticipant(
    participant: ConversationsApiDataParticipant,
    participantsById: Record<string, ConversationParticipant>,
  ): ConversationParticipant {
    const role = this.roleFromType(participant.type);
    return this.getOrBuildParticipant(
      participantsById,
      participant.id,
      role,
      participant.display_name,
    );
  }

  private static buildParticipantId(
    message: ConversationsApiDataMessage,
  ): string {
    if (message.participant_id) {
      return message.participant_id;
    }

    const role = this.roleFromType(message.participant_type);
    return `synthetic-${role}-participant`;
  }

  private static resolveParticipantForMessage(
    message: ConversationsApiDataMessage,
    participantsById: Record<string, ConversationParticipant>,
    user?: NonNullable<StartConversationParams['participant']>,
  ): ConversationParticipant {
    // The client owns user identity (mirrors universal-help v1): a conversation
    // has a single viewer-user, so any user-role message is the current user.
    // This must win over the participantsById lookup, because the server can
    // echo user messages (e.g. channel_menu_submission) with a participant_id
    // that doesn't match the client's conversation.user.id.
    if (user && this.roleFromType(message.participant_type) === 'user') {
      if (!participantsById[user.id]) {
        participantsById[user.id] = user;
      }
      return user;
    }

    if (message.participant_id && participantsById[message.participant_id]) {
      return participantsById[message.participant_id];
    }

    const role = this.roleFromType(message.participant_type);
    const participantId = this.buildParticipantId(message);
    const displayName = role === 'user' ? user?.displayName : undefined;

    return this.getOrBuildParticipant(
      participantsById,
      participantId,
      role,
      displayName,
    );
  }

  private static buildMessagePart(
    part: ConversationsApiDataMessagePart,
  ): MessagePartData {
    const content =
      part.type === 'inline_data_outcome'
        ? {...part.content, text: part.content.text ?? ''}
        : part.content;

    return {
      id: part.id,
      type: part.type,
      content,
      ...(part.dynamic ? {dynamic: part.dynamic} : {}),
    };
  }

  private static buildBaseMessageMetadata(
    message: ConversationsApiDataMessage,
    part?: ConversationsApiDataMessagePart,
  ) {
    const metadata: Record<string, unknown> = {};
    if (message.page_context) {
      metadata.pageContext = message.page_context;
    }
    const dynamic = part?.dynamic || message.dynamic;
    if (dynamic) {
      metadata.dynamic = dynamic;
    }
    if (message.creator_part_id) {
      metadata.creatorPartId = message.creator_part_id;
    }
    if (Object.keys(metadata).length === 0) {
      return undefined;
    }
    return metadata;
  }

  private static enabledInputModes(
    apiMessages: ConversationsApiDataMessage[],
  ): string[] {
    const assistantMessage = this.latestAssistantMessage(apiMessages);
    if (!assistantMessage) {
      return ['text'];
    }

    return this.allowedInputModesFromAssistantMessage(assistantMessage);
  }

  private static latestAssistantMessage(
    apiMessages: ConversationsApiDataMessage[],
  ): ConversationsApiDataMessage | undefined {
    return [...apiMessages]
      .reverse()
      .find((message) => this.roleFromType(message.participant_type) === 'bot');
  }

  private static allowedInputModesFromAssistantMessage(
    assistantMessage: ConversationsApiDataMessage,
  ): string[] {
    return [...(assistantMessage.allowed_input_modes ?? [])];
  }

  static pollInputModesFromApiMessages(
    apiMessages: ConversationsApiDataMessage[],
  ): PollInputModes | undefined {
    const assistantMessage = this.latestAssistantMessage(apiMessages);
    if (!assistantMessage) {
      return undefined;
    }
    return {
      enabledInputModes:
        this.allowedInputModesFromAssistantMessage(assistantMessage),
      messageId: assistantMessage.id,
    };
  }

  private static messagePartId(
    message: ConversationsApiDataMessage,
    part: ConversationsApiDataMessagePart,
  ): string {
    return `${message.id}.${part.id}`;
  }

  private static isGenericRenderablePart(
    part: ConversationsApiDataMessagePart,
  ): boolean {
    return (
      part.type !== 'selected_option' && part.type !== 'inline_data_outcome'
    );
  }

  private static frameworkMessageIdForPart(
    message: ConversationsApiDataMessage,
    part: ConversationsApiDataMessagePart,
  ): string {
    const renderablePartCount = message.parts.filter((candidate) =>
      this.isGenericRenderablePart(candidate),
    ).length;

    if (
      this.roleFromType(message.participant_type) === 'user' &&
      renderablePartCount === 1
    ) {
      return message.id;
    }

    return this.messagePartId(message, part);
  }

  private static parseInlineDataRendering({
    rendering,
    conversationId,
    messageId,
    partId,
    reportWarning,
  }: {
    rendering: string;
    conversationId: string;
    messageId: string;
    partId: string | number;
    reportWarning?: ReportWarning;
  }): ConversationInlineData['data'] {
    try {
      return JSON.parse(rendering) as ConversationInlineData['data'];
    } catch {
      this.reportWarning(
        reportWarning,
        'Conversations API inline data rendering parse failed',
        {
          conversation_id: conversationId,
          message_id: messageId,
          part_id: partId,
          part_type: 'show_inline_data',
          parse_field: 'rendering',
        },
      );
      return [];
    }
  }

  private static stringValue(
    record: Record<string, unknown>,
    ...keys: string[]
  ): string | undefined {
    for (const key of keys) {
      const value = record[key];
      if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }
    return undefined;
  }

  private static isDiscreteChannelMenuSubmission(
    message: ConversationsApiDataMessage,
    part: ConversationsApiDataMessagePart,
    channelMenuPartIds: ReadonlySet<string>,
  ): boolean {
    // Chat and restart behave like button selections: the linked source menu
    // renders the choice. Keep submissions whose source menu is absent as a
    // fallback transcript entry, and preserve RAC/email submissions because
    // they contain form data.
    if (
      !message.creator_part_id ||
      !channelMenuPartIds.has(message.creator_part_id) ||
      part.type !== 'channel_menu_submission'
    ) {
      return false;
    }

    return (
      part.content.channel === 'channel_chat' ||
      part.content.channel === 'channel_restart'
    );
  }

  private static normalizeThumbsFeedback(
    thumbsFeedback?: string,
  ): NonNullable<ConversationMessage['feedback']>['value'] | undefined {
    if (
      thumbsFeedback === 'none' ||
      thumbsFeedback === 'thumbs_up' ||
      thumbsFeedback === 'thumbs_down'
    ) {
      return thumbsFeedback;
    }
    return undefined;
  }

  private static fallbackResponseMetadata(
    message: ConversationsApiDataMessage,
    participant: ConversationParticipant,
  ): Pick<ConversationMessage, 'feedback' | 'responseId'> {
    if (participant.role !== 'bot') {
      return {};
    }

    const feedbackValue = this.normalizeThumbsFeedback(message.thumbs_feedback);
    if (!feedbackValue) {
      return {};
    }

    return {
      responseId: message.id,
      feedback: {
        action: 'log_only',
        value: feedbackValue,
      },
    };
  }

  private static buildResponseMetadata(
    message: ConversationsApiDataMessage,
    participant: ConversationParticipant,
    overrides: Pick<ConversationMessage, 'feedback' | 'responseId'> = {},
  ): Pick<ConversationMessage, 'feedback' | 'responseId'> {
    const fallback = this.fallbackResponseMetadata(message, participant);
    const responseId = overrides.responseId ?? fallback.responseId;
    const feedback = overrides.feedback ?? fallback.feedback;

    return {
      ...(responseId === undefined ? {} : {responseId}),
      ...(feedback === undefined ? {} : {feedback}),
    };
  }

  private static shouldEmitUserOwnedMessage(
    message: ConversationMessage,
  ): boolean {
    return (
      message.type === 'button-options-conversation-message' ||
      message.type === 'select-options-conversation-message'
    );
  }

  private static fromApiPart(
    conversationId: string,
    message: ConversationsApiDataMessage,
    part: ConversationsApiDataMessagePart,
    participantsById: Record<string, ConversationParticipant>,
    isMostRecentPartShowInlineData: boolean,
    user?: NonNullable<StartConversationParams['participant']>,
    attachmentDownloadPath?: string,
    reportWarning?: ReportWarning,
  ): ConversationMessage | null {
    const participant = this.resolveParticipantForMessage(
      message,
      participantsById,
      user,
    );
    const id = this.frameworkMessageIdForPart(message, part);
    const metadata = this.buildBaseMessageMetadata(message, part);
    const timestamp = this.messageTimestamp(message.created);

    if (part.type === 'text') {
      if (message.dynamic) {
        return {
          id,
          participant,
          timestamp,
          data: {
            ...metadata,
            conversationsApi: {messageId: message.id, partId: part.id},
          },
          ...this.buildResponseMetadata(message, participant),
          type: 'custom-part-conversation-message',
          part: {
            id: part.id,
            type: 'ai_response',
            content: part.content,
            dynamic: message.dynamic,
          },
        };
      }
      return {
        id,
        participant,
        timestamp,
        data: {
          ...metadata,
          conversationsApi: {messageId: message.id, partId: part.id},
        },
        ...this.buildResponseMetadata(message, participant),
        type: 'text-conversation-message',
        text: part.content.text,
      };
    }

    if (part.type === 'options') {
      const optionsParticipant =
        part.content.style === 'dropdown' ? participant : user ?? participant;
      const options = part.content.options.map((option) => {
        return {
          id: option.id,
          label: option.text,
          ...(option.external_url ? {externalUrl: option.external_url} : {}),
        };
      });
      const optionsBase = {
        id,
        participant: optionsParticipant,
        timestamp,
        data: {
          ...metadata,
          conversationsApi: {messageId: message.id, partId: part.id},
        },
        label: part.content.text,
        options,
        ...this.buildResponseMetadata(message, participant),
        recommendedOptionId: part.content.recommended_option_id,
        purpose: part.content.purpose,
      };
      if (part.content.style === 'dropdown') {
        return {
          ...optionsBase,
          type: 'select-options-conversation-message',
          defaultOptionId: part.content.preselected_option_id,
        };
      }
      return {
        ...optionsBase,
        type: 'button-options-conversation-message',
      };
    }

    if (part.type === 'selected_option') {
      return null;
    }

    if (part.type === 'inline_data_outcome') {
      return null;
    }

    if (part.type === 'attachment') {
      return {
        id,
        participant,
        timestamp,
        data: metadata,
        type: 'attachment-conversation-message',
        attachment: this.attachmentFromPart(part, attachmentDownloadPath),
      };
    }

    if (part.type === 'show_inline_data') {
      return {
        id,
        participant,
        timestamp,
        data: {
          ...metadata,
          conversationsApi: {messageId: message.id, partId: part.id},
        },
        ...this.buildResponseMetadata(message, participant),
        type: 'inline-data-conversation-message',
        effectId: id,
        inlineData: {
          name: part.content.inline_data_name,
          style: part.content
            .inline_data_style as ConversationInlineData['style'],
          isDone: !isMostRecentPartShowInlineData,
          data: this.parseInlineDataRendering({
            rendering: part.content.rendering,
            conversationId,
            messageId: message.id,
            partId: part.id,
            reportWarning,
          }),
          context: {
            conversation_id: conversationId,
            message_id: message.id,
            message_part_id: part.id,
          },
        },
      };
    }

    if (part.type === 'buttons') {
      return {
        id,
        participant,
        timestamp,
        ...this.buildResponseMetadata(message, participant),
        type: 'rich-text-conversation-message',
        text: part.content.text,
        data: {
          type: 'buttons-conversation-message',
          buttons: part.content.buttons.map((button) => {
            return {
              title: button.title,
              url: button.url,
            };
          }),
        },
      };
    }

    if (part.type === 'articles') {
      const articlesMessage = {
        type: 'articles-conversation-message' as const,
        articleStyle: part.content.article_style,
        articles: part.content.articles.map((article) => {
          return {
            id: article.url,
            title: article.title,
            url: article.url,
            collection: article.collection,
          };
        }),
      };

      return {
        id,
        participant,
        timestamp,
        ...this.buildResponseMetadata(message, participant),
        type: 'articles-conversation-message',
        articleStyle: articlesMessage.articleStyle,
        articles: articlesMessage.articles,
      };
    }

    // A textless `terminate_conversation` part carries no user-facing content,
    // so it's kept out of the visible message list: returning null avoids a
    // phantom `conversation-message-added` event + a "Missing message part
    // component" devLog warning it would otherwise trigger by falling through
    // to a custom part. This is intentionally keyed on empty text, not on a
    // specific reason, so any textless terminator is hidden. Terminators WITH
    // text (transition_to_chat/rac/email) still render their text.
    //
    // Termination handling (Inactive + server-side end) is a SEPARATE concern
    // with a NARROWER scope: `hasBeginCsatTermination` matches only the
    // `begin_csat` reason. Today `begin_csat` is the only textless terminator,
    // so the two coincide; a future textless reason would be hidden here but
    // would NOT get inactive/delete treatment unless added there too. This
    // matches v1, which emits no message for begin_csat.
    if (
      part.type === 'terminate_conversation' &&
      (part.content.text ?? '') === ''
    ) {
      return null;
    }

    const customPart = this.buildMessagePart(part);
    const customPartMetadata =
      part.type === 'channel_menu' || part.type === 'follow_up_case_options'
        ? {
            ...(metadata ?? {}),
            conversationsApi: {messageId: message.id, partId: part.id},
            sourceMessageId: message.id,
            sourcePartId: part.id,
          }
        : {
            ...(metadata ?? {}),
            conversationsApi: {messageId: message.id, partId: part.id},
          };
    return {
      id,
      participant,
      timestamp,
      ...this.buildResponseMetadata(message, participant),
      data: customPartMetadata,
      type: 'custom-part-conversation-message',
      part: customPart,
    };
  }

  private static isMostRecentPartShowInlineData({
    messageCount,
    messageIndex,
    partCount,
    partIndex,
    part,
  }: {
    messageCount: number;
    messageIndex: number;
    partCount: number;
    partIndex: number;
    part: ConversationsApiDataMessagePart;
  }): boolean {
    if (part.type !== 'show_inline_data') {
      return false;
    }

    const isLastMessage = messageIndex === messageCount - 1;
    const isLastPart = partIndex === partCount - 1;

    return isLastMessage && isLastPart;
  }

  private static fallbackSelectedOptionMessage(
    message: ConversationsApiDataMessage,
    part: ConversationsApiDataMessagePart,
    selectedOption: ConversationsApiDataMessagePartContentSelectedOptionPartContent,
    participantsById: Record<string, ConversationParticipant>,
    user?: NonNullable<StartConversationParams['participant']>,
  ): ConversationMessage {
    const participant = this.resolveParticipantForMessage(
      message,
      participantsById,
      user,
    );
    return {
      id: this.messagePartId(message, part),
      participant,
      timestamp: this.messageTimestamp(message.created),
      data: this.buildBaseMessageMetadata(message),
      type: 'text-conversation-message',
      text: selectedOption.text,
    };
  }

  private static attachmentDownloadUrl(
    uploadToken?: string,
    attachmentDownloadPath?: string,
  ): string | undefined {
    if (!uploadToken || !attachmentDownloadPath) {
      return undefined;
    }
    return `${attachmentDownloadPath}?fileId=${encodeURIComponent(
      uploadToken,
    )}`;
  }

  private static attachmentFromPart(
    part: Extract<ConversationsApiDataMessagePart, {type: 'attachment'}>,
    attachmentDownloadPath?: string,
  ) {
    const uploadToken = part.content.upload_token;
    return {
      name: part.content.name,
      size: part.content.size,
      type: part.content.attachment_type,
      ...(uploadToken
        ? {
            upload_token: uploadToken,
            url: this.attachmentDownloadUrl(
              uploadToken,
              attachmentDownloadPath,
            ),
          }
        : {}),
    };
  }

  private static isTextWithAttachmentsInputOptionId(optionId: string): boolean {
    return optionId === 'text-with-attachments-input';
  }

  private static fromApiTextWithAttachmentsMessage(
    message: ConversationsApiDataMessage,
    participantsById: Record<string, ConversationParticipant>,
    attachmentDownloadPath?: string,
    user?: NonNullable<StartConversationParams['participant']>,
  ): ConversationMessage | null {
    const attachmentParts = message.parts.filter(
      (part) => part.type === 'attachment',
    );
    if (attachmentParts.length === 0) {
      return null;
    }

    const participant = this.resolveParticipantForMessage(
      message,
      participantsById,
      user,
    );
    const text = message.parts
      .filter((part) => part.type === 'text')
      .map((part) => part.content.text)
      .join(' ');

    return {
      id: message.id,
      participant,
      timestamp: this.messageTimestamp(message.created),
      data: this.buildBaseMessageMetadata(message),
      type: 'text-with-attachments-conversation-message',
      text,
      attachments: attachmentParts.map((attachmentPart) =>
        this.attachmentFromPart(attachmentPart, attachmentDownloadPath),
      ),
      status: ConversationMessageStatus.Sent,
    };
  }

  static messageTimestamp(created: number): number {
    return created * 1000;
  }

  private static findMostRecentOptionsMessage(
    message: ConversationsApiDataMessage,
    apiMessages: ConversationsApiDataMessage[],
    messagesById: Record<string, ConversationMessage>,
  ): ConversationMessage | undefined {
    const messageIndex = apiMessages.indexOf(message);
    for (let i = messageIndex - 1; i >= 0; i -= 1) {
      const candidate = apiMessages[i];
      for (const part of candidate.parts) {
        const id = this.messagePartId(candidate, part);
        const mapped = messagesById[id];
        if (
          mapped?.type === 'button-options-conversation-message' ||
          mapped?.type === 'select-options-conversation-message'
        ) {
          return mapped;
        }
      }
    }
    return undefined;
  }

  // Collapses an options prompt into its "answered" form: overwrites the entry
  // in messagesById with a user-owned, Sent shape carrying `selectedOptionId`.
  private static markOptionsMessageAnswered({
    optionsMessage,
    selectedOptionId,
    message,
    messagesById,
    participantsById,
    user,
  }: {
    optionsMessage: ConversationMessage | undefined;
    selectedOptionId: string;
    message: ConversationsApiDataMessage;
    messagesById: Record<string, ConversationMessage>;
    participantsById: Record<string, ConversationParticipant>;
    user?: NonNullable<StartConversationParams['participant']>;
  }): boolean {
    if (
      optionsMessage?.type !== 'button-options-conversation-message' &&
      optionsMessage?.type !== 'select-options-conversation-message'
    ) {
      return false;
    }

    const participant =
      user ||
      this.resolveParticipantForMessage(message, participantsById, user);
    messagesById[optionsMessage.id] = {
      ...optionsMessage,
      participant,
      selectedOptionId,
      status: ConversationMessageStatus.Sent,
    };
    return true;
  }

  private static applySelectedOptionParts({
    conversationId,
    apiMessages,
    messagesById,
    participantsById,
    user,
    attachmentDownloadPath,
    reportWarning,
  }: {
    conversationId: string;
    apiMessages: ConversationsApiDataMessage[];
    messagesById: Record<string, ConversationMessage>;
    participantsById: Record<string, ConversationParticipant>;
    user?: NonNullable<StartConversationParams['participant']>;
    attachmentDownloadPath?: string;
    reportWarning?: ReportWarning;
  }): void {
    apiMessages.forEach((message) => {
      message.parts.forEach((part) => {
        if (part.type !== 'selected_option') {
          return;
        }

        const attachmentParts = message.parts.filter(
          (candidate) => candidate.type === 'attachment',
        );
        const optionsMessage =
          (message.creator_part_id
            ? messagesById[message.creator_part_id]
            : undefined) ??
          this.findMostRecentOptionsMessage(message, apiMessages, messagesById);
        const markOptionsMessageSelected = (): boolean =>
          this.markOptionsMessageAnswered({
            optionsMessage,
            selectedOptionId: part.content.id,
            message,
            messagesById,
            participantsById,
            user,
          });

        if (
          this.isTextWithAttachmentsInputOptionId(part.content.id) ||
          attachmentParts.length > 0
        ) {
          markOptionsMessageSelected();
          const participant =
            user ||
            this.resolveParticipantForMessage(message, participantsById, user);
          messagesById[this.messagePartId(message, part)] = {
            id: this.messagePartId(message, part),
            participant,
            timestamp: this.messageTimestamp(message.created),
            data: this.buildBaseMessageMetadata(message),
            type: 'text-with-attachments-conversation-message',
            text: part.content.text,
            attachments: attachmentParts.map((attachmentPart) =>
              this.attachmentFromPart(attachmentPart, attachmentDownloadPath),
            ),
            status: ConversationMessageStatus.Sent,
          };
          return;
        }

        if (part.content.id === 'text-input') {
          markOptionsMessageSelected();
          const participant =
            user ||
            this.resolveParticipantForMessage(message, participantsById, user);
          messagesById[this.messagePartId(message, part)] = {
            id: this.messagePartId(message, part),
            participant,
            timestamp: this.messageTimestamp(message.created),
            data: this.buildBaseMessageMetadata(message),
            type: 'text-conversation-message',
            text: part.content.text,
            status: ConversationMessageStatus.Sent,
          };
          return;
        }

        if (markOptionsMessageSelected()) {
          return;
        }

        this.reportWarning(
          reportWarning,
          'Conversations API selected option fallback used',
          {
            conversation_id: conversationId,
            message_id: message.id,
            part_id: part.id,
            part_type: 'selected_option',
            selected_option_id: part.content.id,
            has_creator_part_id: !!message.creator_part_id,
            creator_part_id: message.creator_part_id,
          },
        );
        const fallbackMessage = this.fallbackSelectedOptionMessage(
          message,
          part,
          part.content,
          participantsById,
          user,
        );
        messagesById[fallbackMessage.id] = fallbackMessage;
      });
    });
  }

  // Collapses the inline data into it's "answered" form.
  // On rehydration we surface that outcome on the inline-data message
  // so the component can restore its selected state across a refresh.
  private static applyInlineDataOutcomes({
    apiMessages,
    messagesById,
  }: {
    apiMessages: ConversationsApiDataMessage[];
    messagesById: Record<string, ConversationMessage>;
  }): void {
    apiMessages.forEach((message) => {
      message.parts.forEach((part) => {
        if (part.type !== 'inline_data_outcome') {
          return;
        }

        const target = message.creator_part_id
          ? messagesById[message.creator_part_id]
          : undefined;
        if (target?.type !== 'inline-data-conversation-message') {
          return;
        }
        messagesById[target.id] = {
          ...target,
          inlineData: {
            ...target.inlineData,
            isDone: true,
            outcome: part.content.outcome ?? target.inlineData.outcome,
          },
        };
      });
    });
  }

  // When the bot shows buttons/select options (e.g. "What do you need help
  // with?"), the user can respond in two ways:
  //   1. Click a button → the API records a `selected_option` part (handled by
  //      `applySelectedOptionParts`, which runs before this method).
  //   2. Type free text instead of clicking → the API records a plain text
  //      part with no reference back to the options prompt.
  //
  // In case 2, we still want the UI to show the options prompt as "answered"
  // (greyed out, non-interactive). This method walks the message history and,
  // for each user message that looks like a free-text reply to an options
  // prompt, marks that prompt as answered with the synthetic id 'text-input'.
  private static applyImplicitOptionAnswers({
    apiMessages,
    messagesById,
    participantsById,
    user,
  }: {
    apiMessages: ConversationsApiDataMessage[];
    messagesById: Record<string, ConversationMessage>;
    participantsById: Record<string, ConversationParticipant>;
    user?: NonNullable<StartConversationParams['participant']>;
  }): void {
    type OptionsPrompt = Extract<
      ConversationMessage,
      {
        type:
          | 'button-options-conversation-message'
          | 'select-options-conversation-message';
      }
    >;

    // As we walk the transcript in order, this tracks the most recent mapped
    // options prompt we've seen. When a later user turn arrives without an
    // explicit selected_option part, this is the prompt we treat that turn as
    // implicitly replying to.
    let lastOptionsMessage: OptionsPrompt | undefined;
    apiMessages.forEach((message) => {
      // Update the tracker before evaluating the current message. Options
      // prompts are discovered from the mapped parts, not by sender role, so
      // the "current prompt in scope" is simply the latest options-part message
      // encountered so far in transcript order.
      for (const part of message.parts) {
        const id = this.messagePartId(message, part);
        const mapped = messagesById[id];
        if (
          mapped?.type === 'button-options-conversation-message' ||
          mapped?.type === 'select-options-conversation-message'
        ) {
          lastOptionsMessage = mapped;
        }
      }

      // Only user turns can answer a prior options prompt.
      if (this.roleFromType(message.participant_type) !== 'user') {
        return;
      }
      // If the API already carried an explicit selected_option part, that
      // concrete answer was handled earlier by applySelectedOptionParts(). Do
      // not replace a real option id (including dropdown choices and pseudo
      // options emitted by other send paths) with the generic text-input
      // fallback here.
      if (message.parts.some((part) => part.type === 'selected_option')) {
        return;
      }
      // If there is no prior prompt in scope, there is nothing to collapse. If
      // the prompt already has selectedOptionId, it was already answered
      // explicitly or by an earlier inferred free-text reply, so do not
      // clobber that existing state.
      if (!lastOptionsMessage || lastOptionsMessage.selectedOptionId) {
        return;
      }

      // v1-parity fallback: a plain user reply immediately following an open
      // options prompt is treated as choosing the text-input pseudo-option.
      // Downstream UI already understands that pseudo-option as "the prompt was
      // answered via typed text", which collapses/hides the original choices
      // and renders the prompt in its answered state.
      this.markOptionsMessageAnswered({
        optionsMessage: lastOptionsMessage,
        selectedOptionId: 'text-input',
        message,
        messagesById,
        participantsById,
        user,
      });
    });
  }

  private static statusFromApi(isActive: boolean): ConversationStatus {
    return isActive ? ConversationStatus.Active : ConversationStatus.Inactive;
  }

  private static findLatestPartMatch<T>(
    apiMessages: ConversationsApiDataMessage[],
    match: (
      part: ConversationsApiDataMessagePart,
      message: ConversationsApiDataMessage,
    ) => T | undefined,
  ): T | undefined {
    for (const message of [...apiMessages].reverse()) {
      for (const part of [...message.parts].reverse()) {
        const result = match(part, message);
        if (result !== undefined) {
          return result;
        }
      }
    }
    return undefined;
  }

  private static racDetailsFromPart(
    part: ConversationsApiDataMessagePart,
  ): RacDetails | undefined {
    if (part.type === 'channel_menu_submission') {
      const channel = this.stringValue(part.content, 'channel');
      const racForm = part.content.rac_form;
      if (
        channel !== 'channel_rac' ||
        !racForm ||
        typeof racForm !== 'object'
      ) {
        return undefined;
      }

      const racFormRecord = racForm as Record<string, unknown>;
      return {
        userName: this.stringValue(racFormRecord, 'name') ?? '',
        userPhoneNumber: this.stringValue(racFormRecord, 'phone_number') ?? '',
        callCanceled: false,
      };
    }

    if (part.type === 'channel_menu') {
      const userName = this.stringValue(part.content, 'initial_name') ?? '';
      const userPhoneNumber =
        this.stringValue(part.content, 'initial_phone_number') ?? '';
      if (userName.length === 0 && userPhoneNumber.length === 0) {
        return undefined;
      }

      return {
        userName,
        userPhoneNumber,
        callCanceled: false,
      };
    }

    return undefined;
  }

  private static channelFromTerminateReason(
    reason: string,
  ): string | undefined {
    switch (reason) {
      case 'transition_to_chat':
        return 'chat';
      case 'transition_to_rac':
        return 'request_a_call';
      case 'transition_to_email':
        return 'email';
      default:
        return undefined;
    }
  }

  private static findLatestTerminateConversation(
    apiMessages: ConversationsApiDataMessage[],
  ):
    | {
        messageId: string;
        partId: number;
        channel: string;
        timestamp: number;
        chatId?: string;
      }
    | undefined {
    return this.findLatestPartMatch(apiMessages, (part, message) => {
      if (part.type !== 'terminate_conversation') {
        return undefined;
      }

      const channel = this.channelFromTerminateReason(part.content.reason);
      if (!channel) {
        return undefined;
      }

      return {
        messageId: message.id,
        partId: part.id,
        channel,
        timestamp: this.messageTimestamp(message.created),
        ...(channel === 'chat' &&
        typeof part.content.external_chat_id === 'string' &&
        part.content.external_chat_id
          ? {chatId: part.content.external_chat_id}
          : {}),
      };
    });
  }

  static hasBeginCsatTermination(
    apiMessages: ConversationsApiDataMessage[],
  ): boolean {
    return (
      this.findLatestPartMatch(apiMessages, (part) => {
        if (part.type !== 'terminate_conversation') {
          return undefined;
        }

        return part.content.reason;
      }) === 'begin_csat'
    );
  }

  private static findLatestRacDetails(
    apiMessages: ConversationsApiDataMessage[],
  ): RacDetails | undefined {
    return this.findLatestPartMatch(apiMessages, (part) =>
      this.racDetailsFromPart(part),
    );
  }

  static deriveChannelContext(
    apiMessages: ConversationsApiDataMessage[],
  ): Partial<ConversationContext> {
    const terminateConversation =
      this.findLatestTerminateConversation(apiMessages);
    if (!terminateConversation) {
      return {};
    }

    switch (terminateConversation.channel) {
      case 'request_a_call': {
        const requestACallDetails = this.findLatestRacDetails(apiMessages);

        return {
          channel: 'request_a_call',
          requestedChannel: 'request_a_call',
          intent: 'contact_support',
          channelTransitionTimestamp: terminateConversation.timestamp,
          ...(requestACallDetails === undefined ? {} : {requestACallDetails}),
        };
      }
      case 'chat':
        return {
          channel: 'chat',
          requestedChannel: 'chat',
          intent: 'contact_support',
          channelTransitionTimestamp: terminateConversation.timestamp,
          // A missing server ID is not evidence that a locally known chat
          // session ended, so omit the key and preserve it when context merges.
          ...(terminateConversation.chatId
            ? {chat_conversationId: terminateConversation.chatId}
            : {}),
        };
      case 'email':
        return {
          channel: 'email',
          requestedChannel: 'email',
          intent: 'contact_support',
          channelTransitionTimestamp: terminateConversation.timestamp,
        };
      default:
        return {};
    }
  }

  static buildContextFromApiMessages(
    apiMessages: ConversationsApiDataMessage[],
  ): Partial<ConversationContext> & {enabledInputModes: string[]} {
    return {
      ...this.deriveChannelContext(apiMessages),
      enabledInputModes: this.enabledInputModes(apiMessages),
    };
  }

  static buildDeltaContextFromApiMessages(
    apiMessages: ConversationsApiDataMessage[],
  ): Partial<ConversationContext> & {enabledInputModes?: string[]} {
    const assistantMessage = this.latestAssistantMessage(apiMessages);

    return {
      ...this.deriveChannelContext(apiMessages),
      ...(assistantMessage
        ? {
            enabledInputModes:
              this.allowedInputModesFromAssistantMessage(assistantMessage),
          }
        : {}),
    };
  }

  static fromApiConversation({
    apiConversation,
    context,
    attachmentDownloadPath,
    user,
    isActive = true,
    reportWarning,
  }: {
    apiConversation: ConversationsApiDataConversation;
    context?: StartConversationParams['context'];
    attachmentDownloadPath?: string;
    user?: NonNullable<StartConversationParams['participant']>;
    isActive?: boolean;
    reportWarning?: ReportWarning;
  }): FrameworkConversationData {
    const participantsById: Record<string, ConversationParticipant> = {};
    if (user) {
      participantsById[user.id] = user;
    }
    apiConversation.participants.forEach((participant) => {
      this.apiParticipantToFrameworkParticipant(participant, participantsById);
    });

    const messagesById = this.messagesByIdFromApi({
      conversationId: apiConversation.id,
      apiMessages: apiConversation.messages,
      participantsById,
      user,
      attachmentDownloadPath,
      reportWarning,
    });

    const triggerPoint =
      apiConversation.trigger_point || context?.triggerParams?.triggerPoint;
    const conversationContext: ConversationContext = {
      ...withConversationOverview(
        apiConversation.id,
        context ?? {},
        apiConversation.overview,
      ),
      audience: apiConversation.audience || context?.audience,
      entryPoint: apiConversation.entry_point || context?.entryPoint,
      channel: 'bot',
      ...this.buildContextFromApiMessages(apiConversation.messages),
    };
    if (triggerPoint) {
      conversationContext.triggerParams = {
        ...(conversationContext.triggerParams ?? {}),
        triggerPoint,
      };
    }

    // todo(@danielxu): temporary solution to pass experiment variant to the client.
    // This should be removed once the experiment is over and the variant is no longer needed.
    if (apiConversation.temporary_experiment_variant) {
      const optionsMessage = Object.values(messagesById)
        .filter((m) => m.type === 'button-options-conversation-message')
        .at(-1);
      if (optionsMessage) {
        messagesById[optionsMessage.id] = {
          ...optionsMessage,
          experimentVariant: apiConversation.temporary_experiment_variant,
        };
      }
    }

    return {
      id: apiConversation.id,
      status: this.statusFromApi(isActive),
      messages: messagesById,
      participants: participantsById,
      context: conversationContext,
    };
  }

  // Rich mapping shared by full-conversation rehydration
  // (`fromApiConversation`) and the send-path message extraction
  // (`visibleMessagesFromApiMessages`). Applies text-with-attachments merging
  // and selected-option collapsing so both entry points produce identical
  // message shapes — mirroring the v1 client's single `effectsToMessages` path.
  private static messagesByIdFromApi({
    conversationId,
    apiMessages,
    participantsById,
    user,
    attachmentDownloadPath,
    reportWarning,
  }: {
    conversationId: string;
    apiMessages: ConversationsApiDataMessage[];
    participantsById: Record<string, ConversationParticipant>;
    user?: NonNullable<StartConversationParams['participant']>;
    attachmentDownloadPath?: string;
    reportWarning?: ReportWarning;
  }): Record<string, ConversationMessage> {
    const messagesById: Record<string, ConversationMessage> = {};
    const channelMenuPartIds = new Set(
      apiMessages.flatMap((message) =>
        message.parts
          .filter((part) => part.type === 'channel_menu')
          .map((part) => `${message.id}.${part.id}`),
      ),
    );
    apiMessages.forEach((message, messageIndex) => {
      const hasSelectedOptionPart = message.parts.some(
        (part) => part.type === 'selected_option',
      );
      if (!hasSelectedOptionPart) {
        const textWithAttachmentsMessage =
          this.fromApiTextWithAttachmentsMessage(
            message,
            participantsById,
            attachmentDownloadPath,
            user,
          );
        if (textWithAttachmentsMessage) {
          messagesById[textWithAttachmentsMessage.id] =
            textWithAttachmentsMessage;
          return;
        }
      }

      message.parts.forEach((part, partIndex) => {
        if (
          this.isDiscreteChannelMenuSubmission(
            message,
            part,
            channelMenuPartIds,
          )
        ) {
          return;
        }
        if (hasSelectedOptionPart && part.type === 'attachment') {
          return;
        }
        const mapped = this.fromApiPart(
          conversationId,
          message,
          part,
          participantsById,
          this.isMostRecentPartShowInlineData({
            messageCount: apiMessages.length,
            messageIndex,
            partCount: message.parts.length,
            partIndex,
            part,
          }),
          user,
          attachmentDownloadPath,
          reportWarning,
        );
        if (mapped) {
          messagesById[mapped.id] = mapped;
        }
      });
    });
    this.applySelectedOptionParts({
      conversationId,
      apiMessages,
      messagesById,
      participantsById,
      user,
      attachmentDownloadPath,
      reportWarning,
    });
    this.applyInlineDataOutcomes({apiMessages, messagesById});
    this.applyImplicitOptionAnswers({
      apiMessages,
      messagesById,
      participantsById,
      user,
    });
    return messagesById;
  }

  // Bare delta mapping for incremental message streams (polling, single-message
  // error updates) where the surrounding conversation isn't present to collapse
  // against.
  static fromApiMessages(
    conversationId: string,
    apiMessages: ConversationsApiDataMessage[],
    participantsById: Record<string, ConversationParticipant>,
    user?: NonNullable<StartConversationParams['participant']>,
    attachmentDownloadPath?: string,
    reportWarning?: ReportWarning,
  ): Record<string, ConversationMessage> {
    const messagesById: Record<string, ConversationMessage> = {};
    const channelMenuPartIds = new Set(
      apiMessages.flatMap((message) =>
        message.parts
          .filter((part) => part.type === 'channel_menu')
          .map((part) => `${message.id}.${part.id}`),
      ),
    );
    apiMessages.forEach((message, messageIndex) => {
      message.parts.forEach((part, partIndex) => {
        if (
          this.isDiscreteChannelMenuSubmission(
            message,
            part,
            channelMenuPartIds,
          )
        ) {
          return;
        }
        const mapped = this.fromApiPart(
          conversationId,
          message,
          part,
          participantsById,
          this.isMostRecentPartShowInlineData({
            messageCount: apiMessages.length,
            messageIndex,
            partCount: message.parts.length,
            partIndex,
            part,
          }),
          user,
          attachmentDownloadPath,
          reportWarning,
        );
        if (mapped) {
          messagesById[mapped.id] = mapped;
        }
      });
    });
    return messagesById;
  }

  static visibleMessagesFromApiMessages({
    conversationId,
    apiMessages,
    participantsById,
    user,
    includeUserMessages = false,
    reportWarning,
    attachmentDownloadPath,
  }: {
    conversationId: string;
    apiMessages: ConversationsApiDataMessage[];
    participantsById: Record<string, ConversationParticipant>;
    user?: NonNullable<StartConversationParams['participant']>;
    includeUserMessages?: boolean;
    reportWarning?: ReportWarning;
    attachmentDownloadPath?: string;
  }): ConversationMessage[] {
    return Object.values(
      this.messagesByIdFromApi({
        conversationId,
        apiMessages,
        participantsById,
        user,
        attachmentDownloadPath,
        reportWarning,
      }),
    ).filter((message) => {
      if (!user || includeUserMessages) {
        return true;
      }

      return (
        message.participant.id !== user.id ||
        this.shouldEmitUserOwnedMessage(message)
      );
    });
  }
}
